import { AntdProTable, ButtonPrimeReact, Dialog } from "@finanedi.es/storybook"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useQuery } from "@tanstack/react-query"
import {
  deleteConciliacionBancariaById,
  getConciliacionBancariaById,
} from "api/ConciliacionBancaria.service"
import { FlujoTesoreriaReadingFiltersDtoProps } from "api/DTOs/FlujoTesoreriaReadingFiltersDto"
import { MovimientoBancarioReadingDtoProps } from "api/DTOs/MovimientoBancarioReadingDto"
import { putFlujoTesoreriaRestaurarConciliacionForzada } from "api/FlujoTesoreria.service"
import { restaurarConciliacionForzada } from "api/MovimientoBancario.service"
import { STATUSCORRECT } from "common/components/ModalVerDocumento/constants"
import notification from "common/utils/notification"
import { MODAL_DOCUMENTOS_CUENTAS_BANCARIAS_CONCILIADAS_COLUMNS_OLD } from "features/Tesoreria/pages/Bancos/pages/BancosDetail/components/ModalDocumentosConciliados/ModalCuentasBancariasColumns"
import ConciliacionManualForzadaTemplate from "features/Tesoreria/pages/Bancos/pages/Conciliacion/components/ForzadaTemplate"
import { DefaultTFuncReturn } from "i18next"
import { Skeleton } from "primereact/skeleton"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { MODAL_DOCUMENTOS_CONCILIADAS_COLUMNS_OLD } from "../ModalDocumentosConciliados/ModalDocumentosConciliadas"

const SkeletonTable = () => (
  <div className='tw-grid tw-grid-cols-3 tw-gap-0.5'>
    {[...Array(6)].map((_, i) => (
      <Skeleton
        key={`skeleton${i + 1}`}
        className='!tw-h-12'
      />
    ))}
  </div>
)

const ConciliadosChildrenModal = ({
  data,
  onHideModalConciliacion,
  handleResetForzosa,
  fetchTable,
  conciliacionBancariaId,
}: {
  data?: MovimientoBancarioReadingDtoProps | null
  conciliacionBancariaId?: string | null
  onHideModalConciliacion: () => void
  handleResetForzosa?: () => void
  fetchTable: () => void
}) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const { data: conciliacionBancariaById, isLoading } = useQuery({
    queryKey: [
      "getConciliacionBancariaById",
      data?.conciliacionBancariaId ?? conciliacionBancariaId,
    ],
    queryFn: () =>
      getConciliacionBancariaById(
        data?.conciliacionBancariaId ?? (conciliacionBancariaId || "")
      ),
    enabled: !!data?.conciliacionBancariaId || !!conciliacionBancariaId,
    refetchOnWindowFocus: false,
    retry: false,
  })

  const desconciliar = async () => {
    const conciliacionId =
      data?.conciliacionBancariaId ?? conciliacionBancariaId
    if (!conciliacionId) {
      return
    }
    setLoading(true)
    const { status }: { status: number } = await deleteConciliacionBancariaById(
      conciliacionId
    )
    if (status === STATUSCORRECT) {
      onHideModalConciliacion()
      fetchTable()
      notification.success({
        body: `${t("bancos.conciliacion.desconciliacionMsg")}`,
      })
    }
    setLoading(false)
  }
  const movimientos =
    conciliacionBancariaById?.movimientosBancarios?.[0]?.cuentaBancaria
  const cuentaNombre = movimientos?.nombre
  const entidadNombre = movimientos?.entidadBancaria?.nombre
  return (
    <div className='tw-mt-2'>
      {data?.conciliacionBancariaId || conciliacionBancariaId ? (
        <div className='tw-grid tw-grid-col-1 lg:tw-grid-cols-2 tw-gap-0.5 tw-bg-neutral-25'>
          {[
            {
              title: t("bancos.conciliacion.labelBankMov"),
              dataSource: conciliacionBancariaById?.movimientosBancarios ?? [],
              columns:
                MODAL_DOCUMENTOS_CUENTAS_BANCARIAS_CONCILIADAS_COLUMNS_OLD(t),
              testId: "tabla-modal-movimientos-bancarios",
            },
            {
              title: t("bancos.conciliacion.modal.conciliadosDoc.header"),
              dataSource: conciliacionBancariaById?.flujosTesoreria ?? [],
              columns: MODAL_DOCUMENTOS_CONCILIADAS_COLUMNS_OLD(t),
              testId: "tabla-modal-documentos-conciliados",
            },
          ].map((table, index) => (
            <section
              key={`${index + 1}`}
              className='tw-w-112 lg:tw-w-160 tw-h-60 lg:tw-h-119 tw-overflow-y-hidden tw-bg-background'
            >
              <div className='tw-flex tw-flex-row tw-h-14 tw-items-center tw-pl-5 tw-pr-6 tw-bg-white'>
                <span className='tw-ml-2 tw-select-none tw-font-medium'>
                  {table.title}
                </span>
                <span className='tw-ml-1 tw-select-none'>
                  {index === 0 && (
                    <>
                      - {t("bancos.conciliacion.modal.conciliadosDoc.account")}
                      {cuentaNombre
                        ? ` ${cuentaNombre}`
                        : ` ${entidadNombre ?? ""}`}
                    </>
                  )}
                </span>
              </div>
              <div className='tw-scrollbar-thin tw-h-full tw-overflow-y-auto'>
                {isLoading ? (
                  <SkeletonTable />
                ) : (
                  <AntdProTable
                    data-testid={table.testId}
                    id={table.testId}
                    rowKey='id'
                    className='tw-w-full'
                    defaultSelectionModeAll={false}
                    columns={table.columns}
                    dataSource={table.dataSource}
                    rowSelection={false}
                    pagination={false}
                  />
                )}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className='tw-flex tw-justify-center tw-items-center tw-w-180 tw-h-119'>
          <ConciliacionManualForzadaTemplate onConfirm={handleResetForzosa} />
        </div>
      )}

      <div className='tw-w-full tw-flex tw-justify-end tw-mt-6'>
        <div className='tw-flex tw-gap-4'>
          <ButtonPrimeReact
            onClick={onHideModalConciliacion}
            className='tw-px-6 !tw-bg-transparent !tw-text-text hover:!tw-shadow !tw-border-none'
          >
            {t("bancos.conciliacion.modal.cancelButton")}
          </ButtonPrimeReact>
          {(Boolean(data?.conciliacionBancariaId) ||
            conciliacionBancariaId) && (
            <ButtonPrimeReact
              onClick={desconciliar}
              loading={loading}
              className='tw-px-6 hover:!tw-shadow tw-flex tw-items-center'
            >
              {t("bancos.conciliacion.modal.unConciliarButton")}
            </ButtonPrimeReact>
          )}
        </div>
      </div>
    </div>
  )
}

const ModalConciliacionMovimientos = ({
  showModal,
  data,
  conciliacionBancariaId,
  onHideModalConciliacion,
  fetchTable,
  i18nModalConfirmDescription,
}: {
  showModal: boolean
  data?:
    | MovimientoBancarioReadingDtoProps
    | FlujoTesoreriaReadingFiltersDtoProps
    | null
  conciliacionBancariaId?: string | null
  onHideModalConciliacion: () => void
  fetchTable: () => void
  i18nModalConfirmDescription?: DefaultTFuncReturn
}) => {
  const { t } = useTranslation()
  const [showModalAtencion, setShowModalAtencion] = useState(false)
  const handleConfirmForzosa = async () => {
    if (data) {
      try {
        "idExterno" in data && data?.idExterno
          ? await restaurarConciliacionForzada({
              movimientoBancarioIdsExternos: [data.idExterno],
            })
          : !("idExterno" in data) &&
            data.id &&
            (await putFlujoTesoreriaRestaurarConciliacionForzada({
              flujoTesoreriaIds: [data.id],
            }))
        notification.success({
          body: `${t("bancos.conciliacion.desconciliacionMsg")}`,
        })
        setShowModalAtencion(false)
        onHideModalConciliacion()
        fetchTable()
      } catch {
        // eslint-disable-next-line no-console
        console.log("error")
      }
    }
  }
  return (
    <div>
      <Dialog
        visible={showModal}
        className='tw-w-auto tw-rounded-lg tw-p-6 tw-bg-white'
        baseZIndex={20}
        closable={false}
        keepInViewport={false}
        header={t("pagoCobro.tab.headerReconcile")}
        onHide={onHideModalConciliacion}
      >
        <ConciliadosChildrenModal
          data={data}
          conciliacionBancariaId={conciliacionBancariaId}
          onHideModalConciliacion={onHideModalConciliacion}
          fetchTable={fetchTable}
          handleResetForzosa={() => setShowModalAtencion(!showModalAtencion)}
        />
      </Dialog>
      <Dialog
        visible={showModalAtencion}
        className='tw-w-auto tw-rounded-lg tw-p-6 tw-bg-white'
        baseZIndex={30}
        closable={false}
        keepInViewport={false}
        header={false}
        onHide={() => setShowModalAtencion(!showModalAtencion)}
      >
        <div>
          <div className='tw-mb-4 tw-flex tw-w-full tw-justify-center'>
            <div className='tw-flex tw-h-24 tw-w-24 tw-items-center tw-justify-center tw-rounded-full tw-bg-background'>
              <div className='tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-border tw-bg-white'>
                <FontAwesomeIcon
                  icon={["fal", "question"]}
                  className='tw-h-8 tw-text-text-hyperlink'
                />
              </div>
            </div>
          </div>
          <div className='tw-mb-10 tw-flex tw-w-full tw-flex-col tw-items-center'>
            <p className='tw-mb-3 tw-text-xl tw-font-medium'>
              {t("bancos.conciliacion.modal.restaurarConciliado.title")}
            </p>
            <p className='tw-mb-0 tw-text-lg'>
              {i18nModalConfirmDescription ??
                t("bancos.conciliacion.modal.restaurarConciliado.text")}
            </p>
          </div>
          <div className='tw-mb-1 tw-flex tw-w-full tw-flex-row'>
            <ButtonPrimeReact
              onClick={() => setShowModalAtencion(!showModalAtencion)}
              className='!tw-mr-1.5 !tw-flex !tw-h-12 !tw-w-44 !tw-items-center !tw-justify-center !tw-border-transparent !tw-bg-transparent !tw-text-text hover:!tw-shadow'
            >
              {t("bancos.conciliacion.modal.restaurarConciliado.cancel")}
            </ButtonPrimeReact>
            <ButtonPrimeReact
              className='!tw-ml-1.5 !tw-flex !tw-h-12 !tw-w-44 !tw-items-center !tw-justify-center !tw-rounded !tw-border-transparent !tw-bg-primary !tw-text-white hover:!tw-bg-primary-over hover:!tw-shadow'
              onClick={() => handleConfirmForzosa()}
            >
              {t("bancos.conciliacion.modal.restaurarConciliado.continuar")}
            </ButtonPrimeReact>
          </div>
        </div>
      </Dialog>
    </div>
  )
}

export default ModalConciliacionMovimientos
